// Infrastruktur-Hub-Page — Tab-Layout (TQ #5340, Refactor von Card-Grid #5310).
//
// Tab-Bar oben: [Geraete | IaaS]. Inhalt inline. URL-Query ?tab= synchronisiert,
// damit Refresh und Deeplink funktionieren. Backwards-Compat-Redirects fuer
// /infra/devices und /devices sind in App.tsx Routes geregelt.

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import Devices from './Devices'
import { CloudIcon } from '../components/ui/Icons'
import { isPersonalMode } from '../utils/cockpitMode'
import './InfraPage.css'

interface Organization {
  id: number
  name: string
  slug: string
    public_token?: string
  member_role: string
}

interface InfraPageProps {
  organization: Organization
}

type TabId = 'devices' | 'iaas'

const TABS: { id: TabId; label: string }[] = [
  { id: 'devices', label: 'Geräte' },
  { id: 'iaas', label: 'IaaS' },
]

interface DeviceSummary {
  device_id: string
  status: string
}

interface DevicesResponse {
  devices: DeviceSummary[]
}

function IaasTab({ organization }: { organization: Organization }) {
  // Phase 1: Org hat noch keine IaaS-Subscription (kommt mit IaaS-Produkt-Launch).
  // Wir zeigen Empty-State mit Inline-Sales-Info — KEIN Redirect raus, wie #5310
  // urspruenglich machte.
  return (
    <div className="infra-iaas-tab">
      <h2 className="infra-section-title">IaaS-Ressourcen</h2>
      <p className="infra-section-lead">
        Hier verwaltet {organization.name} gebuchte Server und virtuelle Maschinen.
      </p>

      <div className="infra-iaas-empty">
        <div className="infra-iaas-empty-icon">
          <CloudIcon size={48} />
        </div>
        <h3>Noch keine IaaS-Ressourcen gebucht</h3>
        <p>
          Buche eine VM oder einen Server für dein Unternehmen. Aria kann sie
          später direkt im Cockpit verwalten.
        </p>
        <div className="infra-iaas-package">
          <strong>VM Standard</strong>
          <span>4 vCPU · 16 GB RAM · 100 GB SSD</span>
          <span className="infra-iaas-price">ab 49 €/Monat</span>
        </div>
        <button
          type="button"
          className="infra-iaas-cta"
          onClick={() => {
            // IaaS-Buchungs-Flow ist out-of-scope #5340. Vorlaeufig:
            // freundlicher Hinweis statt Redirect, bis das Buchungs-Modal
            // gebaut ist (eigener TQ).
            alert(
              'Buchungs-Flow folgt in Kürze. Bis dahin kontaktiere uns direkt unter info@it-sce.de.',
            )
          }}
        >
          IaaS-Pakete ansehen
        </button>
      </div>
    </div>
  )
}

function DevicesTab({ organization, personalMode }: { organization: Organization; personalMode: boolean }) {
  // Wir reichen die existing Devices-Page unveraendert ein. personalMode steuert
  // user-scoped (/api/me/devices) vs org-scoped (/api/org/<slug>/devices).
  return <Devices organization={organization} personalMode={personalMode} />
}

export default function InfraPage({ organization }: InfraPageProps) {
  const { api } = useAuth()
  // TQ #6962-T4: Personal-Mode (with.it-sce.de) → user-scoped Geräte.
  const personalMode = isPersonalMode()
  const [params, setParams] = useSearchParams()
  const queryTab = params.get('tab') as TabId | null
  const activeTab: TabId = queryTab === 'iaas' || queryTab === 'devices' ? queryTab : 'devices'

  const [devicesCount, setDevicesCount] = useState<{ online: number; offline: number } | null>(null)

  const loadDevicesCount = useCallback(async () => {
    try {
      const url = personalMode
        ? '/api/me/devices'
        : `/api/org/${organization.public_token || organization.slug}/devices`
      const r = await api!.get<DevicesResponse>(url)
      const devs = r.data?.devices ?? []
      setDevicesCount({
        online: devs.filter(d => d.status === 'online').length,
        offline: devs.filter(d => d.status !== 'online').length,
      })
    } catch {
      setDevicesCount(null)
    }
  }, [api, personalMode, organization.slug, organization.public_token])

  useEffect(() => {
    void loadDevicesCount()
  }, [loadDevicesCount])

  const setTab = (tab: TabId) => {
    const next = new URLSearchParams(params)
    if (tab === 'devices') {
      // 'devices' ist Default — Query-Param weglassen damit URL clean bleibt
      next.delete('tab')
    } else {
      next.set('tab', tab)
    }
    setParams(next, { replace: false })
  }

  const devicesBadge = devicesCount
    ? `${devicesCount.online}/${devicesCount.online + devicesCount.offline}`
    : null

  return (
    <div className="infra-page">
      <div className="page-header">
        <h1 className="page-title">Infrastruktur</h1>
      </div>

      <div className="infra-tabs" role="tablist">
        {TABS.map(t => (
          <button
            key={t.id}
            role="tab"
            aria-selected={activeTab === t.id}
            className={`infra-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span>{t.label}</span>
            {t.id === 'devices' && devicesBadge && (
              <span className="infra-tab-badge">{devicesBadge}</span>
            )}
          </button>
        ))}
      </div>

      <div className="infra-tab-content" role="tabpanel">
        {activeTab === 'devices' && <DevicesTab organization={organization} personalMode={personalMode} />}
        {activeTab === 'iaas' && <IaasTab organization={organization} />}
      </div>
    </div>
  )
}
